import { useMemo, useState } from 'react';
import { Mail, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@ncoder/ui';
import {
  useOrgInvitations,
  useOrgRoles,
  useCreateOrgInvitation,
  useResendOrgInvitation,
  useCancelOrgInvitation,
} from '@/hooks/org-admin';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { SegmentedControl } from '@/components/molecules/SegmentedControl';
import { InvitationCard, InvitationRow } from '@/components/molecules/org-admin/InvitationRow';
import { InviteMemberModal } from '@/components/molecules/org-admin/InviteMemberModal';
import {
  OrgAdminEmptyState,
  OrgAdminErrorState,
  OrgAdminNoResults,
  OrgAdminTableSkeleton,
} from '@/components/molecules/org-admin/OrgAdminStates';
import {
  OA_NARROW_BREAKPOINT,
  PlainHeader,
  SortableHeader,
  sortRows,
  useContainerWidth,
  useSort,
  useTextFilter,
} from '@/components/molecules/org-admin/OrgAdminTable';
import { OrgAdminResultCount, OrgAdminToolbar } from '@/components/molecules/org-admin/OrgAdminToolbar';
import { NO_PERMISSION_REASON, type OrgAdminSelfContext } from '@/components/molecules/org-admin/types';
import type { OrgInvitation } from '@/services/org-admin';

export interface InvitationsPanelProps {
  self: OrgAdminSelfContext;
}

type InvitationSortKey = 'email' | 'status' | 'role' | 'expires';

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'all', label: 'All' },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

export function InvitationsPanel({ self }: InvitationsPanelProps) {
  const { data: invitations = [], isLoading, isError, error, refetch } = useOrgInvitations();
  const { data: roles = [] } = useOrgRoles();
  const createInvitation = useCreateOrgInvitation();
  const resendInvitation = useResendOrgInvitation();
  const cancelInvitation = useCancelOrgInvitation();
  const [confirmModal, confirmModalContext] = useConfirmModal();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Pending is the default view: an invitation that has already been accepted or cancelled is
  // history, and history was previously mixed into the same undifferentiated list as the open loops.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const { sort, toggle } = useSort<InvitationSortKey>({ key: 'expires', direction: 'asc' });

  const { ref: bodyRef, width } = useContainerWidth<HTMLDivElement>();
  const isNarrow = width !== null && width < OA_NARROW_BREAKPOINT;

  const roleNameById = useMemo(() => new Map(roles.map((role) => [role.id, role.name])), [roles]);
  const roleNameOf = (invitation: OrgInvitation) =>
    invitation.role_id ? roleNameById.get(invitation.role_id) ?? null : null;

  const statusFiltered = useMemo(
    () =>
      statusFilter === 'pending'
        ? invitations.filter((invitation) => invitation.status === 'pending')
        : invitations,
    [invitations, statusFilter],
  );

  const searched = useTextFilter(statusFiltered, query, (invitation) => [
    invitation.email,
    roleNameOf(invitation),
  ]);

  const visible = useMemo(
    () =>
      sortRows(searched, sort, (invitation, key) => {
        switch (key) {
          case 'email':
            return invitation.email;
          case 'status':
            return invitation.status;
          case 'role':
            return roleNameOf(invitation);
          case 'expires':
            // Sort on the timestamp, not the rendered "in 5d" string, which would order lexically.
            return Date.parse(invitation.expires_at) || 0;
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searched, sort, roleNameById],
  );

  const handleInviteSubmit = async (data: Parameters<typeof createInvitation.mutateAsync>[0]) => {
    try {
      await createInvitation.mutateAsync(data);
      toast.success('Invitation sent');
      setInviteOpen(false);
      // A newly created invitation is pending, so surface it even if the admin was browsing history.
      setStatusFilter('pending');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to send invitation');
    }
  };

  const handleResend = (invitationId: string) => {
    resendInvitation.mutate(invitationId, {
      onSuccess: () => toast.success('Invitation resent'),
      onError: (err) => toast.error((err as Error).message || 'Failed to resend invitation'),
    });
  };

  const handleCancel = async (invitationId: string) => {
    const invitation = invitations.find((candidate) => candidate.id === invitationId);
    const confirmed = await confirmModal.error({
      title: 'Cancel invitation?',
      content: `The link sent to ${invitation?.email ?? 'this address'} will stop working. You can send a new invitation to the same address afterwards.`,
      okText: 'Cancel invitation',
      cancelText: 'Keep it',
      okButtonProps: { variant: 'destructive' },
    });
    if (!confirmed) return;
    cancelInvitation.mutate(invitationId, {
      onSuccess: () => toast.success('Invitation cancelled'),
      onError: (err) => toast.error((err as Error).message || 'Failed to cancel invitation'),
    });
  };

  const inviteButton = (
    <Button
      size="sm"
      className="h-8"
      onClick={() => setInviteOpen(true)}
      disabled={!self.canManage}
      title={self.canManage ? undefined : NO_PERMISSION_REASON}
    >
      <Plus className="h-4 w-4" aria-hidden />
      Invite member
    </Button>
  );

  const canFilter = !isLoading && !isError && invitations.length > 0;

  const rowProps = (invitation: OrgInvitation) => ({
    invitation,
    roleName: roleNameOf(invitation),
    canManage: self.canManage,
    isResending: resendInvitation.isPending && resendInvitation.variables === invitation.id,
    isCancelling: cancelInvitation.isPending && cancelInvitation.variables === invitation.id,
    onResend: handleResend,
    onCancel: handleCancel,
  });

  return (
    <div className="oa-panel mb-3 mt-3 flex min-h-0 flex-1 flex-col">
      {/* Search and filters only when there is something to search — see MembersPanel. The action
          stays, because inviting is exactly what an admin came here to do when the list is empty. */}
      <OrgAdminToolbar
        searchPlaceholder={canFilter ? 'Search invitations by email or role' : undefined}
        searchValue={query}
        onSearchChange={canFilter ? setQuery : undefined}
        filters={
          canFilter && (
            <SegmentedControl
              ariaLabel="Filter invitations by status"
              value={statusFilter}
              options={STATUS_FILTERS}
              onChange={setStatusFilter}
              size="sm"
            />
          )
        }
        action={inviteButton}
      />

      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <OrgAdminTableSkeleton
            rows={5}
            columns={['34%', '84px', '110px', '96px']}
            label="Loading invitations…"
          />
        ) : isError ? (
          <OrgAdminErrorState
            title="Couldn't load invitations"
            error={error}
            onRetry={() => void refetch()}
          />
        ) : invitations.length === 0 ? (
          <OrgAdminEmptyState
            icon={Mail}
            title="No invitations yet"
            description="Invite someone by email and choose the role they join with. They appear in Members once they accept."
            action={inviteButton}
          />
        ) : visible.length === 0 ? (
          <OrgAdminNoResults
            query={query}
            onClear={() => {
              setQuery('');
              setStatusFilter('all');
            }}
          />
        ) : isNarrow ? (
          <div>
            {visible.map((invitation) => (
              <InvitationCard key={invitation.id} {...rowProps(invitation)} />
            ))}
          </div>
        ) : (
          <table className="oa-table">
            <caption className="sr-only">
              Organization invitations, their status, the role offered and when they expire
            </caption>
            <thead>
              <tr>
                <SortableHeader columnKey="email" label="Invitee" sort={sort} onSort={toggle} />
                <SortableHeader columnKey="status" label="Status" sort={sort} onSort={toggle} width="112px" />
                <SortableHeader columnKey="role" label="Role" sort={sort} onSort={toggle} width="150px" />
                <SortableHeader columnKey="expires" label="Expiry" sort={sort} onSort={toggle} width="132px" />
                <PlainHeader label="Actions" align="right" width="76px" srOnly />
              </tr>
            </thead>
            <tbody>
              {visible.map((invitation) => (
                <InvitationRow key={invitation.id} {...rowProps(invitation)} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!isLoading && !isError && invitations.length > 0 && (
        <OrgAdminResultCount shown={visible.length} total={invitations.length} noun="invitation" />
      )}

      <InviteMemberModal
        open={inviteOpen}
        roles={roles}
        currentUserRole={self.currentUserRole}
        isSubmitting={createInvitation.isPending}
        onSubmit={handleInviteSubmit}
        onClose={() => setInviteOpen(false)}
      />
      {confirmModalContext}
    </div>
  );
}
